import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Map from './Map';
import { Layout, Switch, Tooltip, Row } from 'antd';
import { Tab } from '@hydrodigital/design-system';
import KpiSvg from '@/assets/svg/KpiSvg';
import InfrastructureDashboardView from './Infrastructure';
import Icon, { CloseOutlined } from '@ant-design/icons';
import './index.scss';
import DashboardContext from '@/contexts/DashboardContext';
import WaveSvg from '@/assets/svg/WaveSvg';
import KpiService from '@/domain/Kpi/KpiService';
import dayjs from 'dayjs';
import { dashboardMapEventsService } from '@/events/dashboardMap.subject';
import { lateralMenuEventsService } from '@/events/lateralMenu.subject';

// import KpiCard from '@/components/Dashboard/KpiCard';

import KpiDashBoardView from './Kpi';
import LayerList from '@/components/Dashboard/LayerList.tsx';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedInfrastructureTypes } from '@/redux/slices/dashboardSlice';
import { SidePanel } from '@/components/Dashboard/SidePanel';
import { DigitalTwinsContainer } from '@/components/Dashboard/DigitalTwins';
import { DigitalTwinsContextProvider } from '@/contexts/DigitalTwinsContext';
import { MmsSidePanel } from '@/components/Dashboard/MmsMode';
import { PastOverflowSidePanel } from '@/components/Dashboard/PastOverflow';
import { useMmsMapData } from './hooks/useMmsMapData';
import { m } from '@/paraglide/messages';
import { useLocale } from '@/contexts/LocaleContext';
import {
	SensorMaintenanceLensProvider,
	SensorMaintenanceLensSidebar,
} from '@/features/sensorMaintenanceLens';

import { KpiCard } from '@hydrodigital/kpicard';


const kpiService = new KpiService();
const { Sider } = Layout;

function DashboardContent() {
	useLocale();
	const subscriptionLM$ = lateralMenuEventsService.getSubject();
	const { value: context, setValue: setContext } = useContext(DashboardContext);

	// Single shared useMmsMapData instance � passed to both Map and MmsSidePanel
	const mmsMapData = useMmsMapData({
		mmsId: context.selectedMmsId,
		fileName: context.selectedMmsFileName,
		locationId: context.selectedLocation,
		enabled: !!context.activatedMmsMode,
	});

	const [tabActive, setTabActive] = useState<string | null>(null);
	const [futureRainfall, setFutureRainfall] = useState<any>(false);
	const [showRainfall, setShowRainfall] = useState(false);
	const oui = JSON.parse(localStorage.getItem('security') ?? '{}').organizationId;
	const userId = localStorage.getItem('userId') ?? '';
	const [pinnedKpis, setPinnedKpis] = useState<any[]>([]);
	const [kpis, setKpis] = useState<any[]>([]);
	const infrastructure = useSelector((state: any) => state.dashboard.infrastructure);
	const dispatch = useDispatch();
	const previousLocationRef = useRef(context.selectedLocation);
	const [searchParams, setSearchParams] = useSearchParams();

	// Prevent the browser Back button from leaving the Home page.
	// Capture phase (true) fires before React Router's bubble-phase listener.
	// stopImmediatePropagation() blocks React Router from seeing the event.
	// pushState restores the current URL so the address bar doesn't change.
	useEffect(() => {
		window.history.pushState(null, '', window.location.href);
		const handlePopState = (event: PopStateEvent) => {
			event.stopImmediatePropagation();
			window.history.pushState(null, '', window.location.href);
		};
		window.addEventListener('popstate', handlePopState, true);
		return () => window.removeEventListener('popstate', handlePopState, true);
	}, []);

	// Activate MMS mode when arriving from ModelTable redirect (?mmsMode=true&mmsId=...&fileName=...)
	useEffect(() => {
		const mmsMode = searchParams.get('mmsMode');
		const mmsId = searchParams.get('mmsId');
		const fileName = searchParams.get('fileName');
		if (mmsMode === 'true' && mmsId) {
			setContext((prev) => ({
				...prev,
				activatedMmsMode: true,
				activatedDigitalTwinMode: false,
				activatedInflowMode: false,
				activatedSewerAnalysisMode: false,
				selectedMmsId: mmsId,
				selectedMmsFileName: fileName ?? undefined,
			}));
			setSearchParams({}, { replace: true });
		}
	}, []);

	const currentInfrastructureTypes: any[] = useMemo(() => {
		return Array.from(
			new Set(
				infrastructure.map((infra) => {
					return infra.infrastructureTypeName;
				}),
			),
		);
	}, [infrastructure]);

	useEffect(() => {
		handleGetKpis();
	}, [oui, userId, context.selectedLocation]);

	// Deactivate all active modes when location changes
	useEffect(() => {
		const locationChanged = previousLocationRef.current !== context.selectedLocation;

		if (locationChanged) {
			// Update the ref immediately
			previousLocationRef.current = context.selectedLocation;

			if (context.activatedDigitalTwinMode) {
				setContext((prev) => ({ ...prev, activatedDigitalTwinMode: false }));
			}

			if (context.activatedInflowMode) {
				setContext((prev) => ({ ...prev, activatedInflowMode: false }));
			}

			if (context.activatedMmsMode) {
				setContext((prev) => ({
					...prev,
					activatedMmsMode: false,
					selectedMmsId: undefined,
					selectedMmsFileName: undefined,
				}));
			}

			if (context.activatedPastOverflowMode) {
				setContext((prev) => ({ ...prev, activatedPastOverflowMode: false }));
			}

			if (context.activatedSewerAnalysisMode) {
				setContext((prev) => ({ ...prev, activatedSewerAnalysisMode: false }));
			}
		}
	}, [context.selectedLocation]);

	const onChange = (key: string) => {
		setTabActive(key);
	};

	const setActive = (tab: string) => {
		setContext((prevContext) => ({ ...prevContext, expandLeftMenu: true }));
		setTabActive(tab);
	};

	const handlePinKpi = async (kpi: any) => {
		const resPinKpi = await kpiService.postPinKpiInDashboard(kpi.id, !kpi.show);

		if (resPinKpi?.success) {
			// Update the local state
			const updatedKpis = kpis.map((item) =>
				item.id === kpi.id ? { ...item, show: !kpi.show } : item,
			);
			setKpis(updatedKpis);

			// Update pinned KPIs
			console.log('kpi.show', kpi.show);

			if (kpi.show) {
				setPinnedKpis((prev) => prev.filter((item) => item.id !== kpi.id));
			} else {
				setPinnedKpis((prev) => [...prev, { ...kpi, show: true }]);
			}
		}
	};

	const handleGetKpis = async () => {
		if (oui && userId) {
			try {
				const resp = await kpiService.getKpi(oui, userId, context.selectedLocation);
				setKpis(resp);
				setPinnedKpis(resp.filter((card: any) => card?.show === true));
			} catch (error) {
				console.error('Error fetching KPIs:', error);
				setKpis([]);
				setPinnedKpis([]);
			}
		} else {
			setKpis([]);
			setPinnedKpis([]);
		}
	};

	const tabs = [
		{
			id: 'INFRASTRUCTURE',
			label: 'ASSETS',
			content: <InfrastructureDashboardView />,
		},
		{
			id: 'KPI',
			label: 'KPIs',
			content: (
				<KpiDashBoardView
					kpis={kpis}
					pinnedKpis={pinnedKpis}
					handleGetKpis={handleGetKpis}
					handlePinKpi={handlePinKpi}
				/>
			),
		},
	];

	const getOptions = (values: any) => ({
		title: { text: '' },
		credits: { enabled: false },
		chart: { type: 'line', width: 88, height: 89, backgroundColor: '#F7F9FC' },
		xAxis: { title: { enabled: false }, labels: { enabled: false }, tickLength: 0 },
		yAxis: { title: { enabled: false }, labels: { enabled: false }, tickLength: 0 },
		legend: { enabled: false },
		series: [{ data: Object.values(values) }],
	});

	const orderData = (data: Record<string, any>) => {
		const entries = Object.entries(data);
		entries.sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
		return Object.fromEntries(entries);
	};

	const getLastKeyValue = (data: any) => {
		const keys = Object.keys(data).sort();
		const lastKey = keys[keys.length - 1];
		const date = new Date(parseInt(lastKey)).toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
		});
		const value = data[lastKey];
		return { date, value };
	};

	const objectToArray = (values: any, first?: boolean, last?: boolean) => {
		let value: any;

		if (Object.entries(values).length !== 0) {
			const arregloOrdenado: any = Object.entries(values).sort(
				(a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime(),
			);

			if (first) {
				if (arregloOrdenado[0][0] !== '') {
					value = dayjs(arregloOrdenado[0][0]).format('h:mm a');
				} else {
					value = 'no data available';
				}
			}
			if (last) {
				if (arregloOrdenado[0][1] > 0) {
					value = arregloOrdenado[0][1];
				} else {
					value = '0';
				}
			}
		} else {
			if (first) value = 'no data available';
			if (last) value = '0';
		}

		return value;
	};

	useEffect(() => {
		subscriptionLM$.subscribe((event) => {
			if (event.type == 'setActive') {
				setActive(event.data);
			}
		});
	}, []);

	const handleCloseDigitalTwins = () => {
		dashboardMapEventsService.setSubject({ type: 'closePopup' });
		setContext((prevContext) => ({ ...prevContext, activatedDigitalTwinMode: false }));
		dashboardMapEventsService.setSubject({ type: 'setMode', data: 'default' });
	};

	const handleClosePastOverflow = () => {
		dashboardMapEventsService.setSubject({
			type: 'closePopup',
		});

		setContext((prevContext) => ({
			...prevContext,
			activatedPastOverflowMode: false,
		}));

		dashboardMapEventsService.setSubject({
			type: 'setMode',
			data: 'default',
		});
	};

	const handleCloseMms = () => {
		// Close any open popup
		dashboardMapEventsService.setSubject({
			type: 'closePopup',
		});

		setContext((prevContext) => ({
			...prevContext,
			activatedMmsMode: false,
			selectedMmsId: undefined,
			selectedMmsFileName: undefined,
			lastCompletedSimulationId: undefined,
			lastCompletedGroupName: undefined,
			mmsCheckedAssets: [],
		}));

		// Reflect "Map" mode in the header
		dashboardMapEventsService.setSubject({
			type: 'setMode',
			data: 'default',
		});
	};

	const handleCloseSensorMaintenance = () => {
		dashboardMapEventsService.setSubject({ type: 'closePopup' });
		setContext((prevContext) => ({
			...prevContext,
			activatedSensorMaintenanceMode: false,
		}));
		dashboardMapEventsService.setSubject({
			type: 'setMode',
			data: 'default',
		});
	};

	return (
		<>
			<Map mmsMapData={mmsMapData} />

			<div
				style={{
					position: 'absolute',
					right: context.showNotifications ? '275px' : '2px',
					bottom: '21px',
					width: '260px',
					height: '90px',
				}}
			>
				<Switch
					onClick={(val: any) => {
						dashboardMapEventsService.setSubject({ type: 'changeAnimation', data: val });
					}}
					style={{ position: 'absolute', right: '20px', bottom: '32px' }}
					checkedChildren="Flows"
					unCheckedChildren="Flows"
				/>
				<Switch
					onChange={(checked) => {
						setShowRainfall(checked);
						if (!checked) {
							dashboardMapEventsService.setSubject({ type: 'cleanRainfallLayer' });
						} else {
							dashboardMapEventsService.setSubject({
								type: 'drawRainfallLayer',
								data: { futureRainfall },
							});
						}
					}}
					style={{
						position: 'absolute',
						right: '150px',
						bottom: '32px',
						width: '104px',
					}}
					checkedChildren="Radar"
					unCheckedChildren="Radar"
				/>
				{showRainfall && (
					<Switch
						checked={futureRainfall}
						onChange={(checked) => {
							setFutureRainfall(checked);
							dashboardMapEventsService.setSubject({
								type: 'futureRainfallToggleChanged',
								data: { futureRainfall: checked },
							});
						}}
						style={{
							position: 'absolute',
							right: '150px',
							bottom: '5px',
							width: '104px',
						}}
						checkedChildren="Future"
						unCheckedChildren="Past"
					/>
				)}
			</div>

			{/* <WeatherMap/> */}
			<Layout className="site-layout" style={{ background: 'white', overflowY: 'auto' }}>
				<Sider
					style={{
						width: 56,
						background: 'white',
						position: 'absolute',
						zIndex: 9,
						top: 75,
						left: 10,
						borderRadius: 5,
						display: !context.expandLeftMenu ? 'block' : 'none',
						overflow: 'auto',
					}}
					width={56}
				>
					<div style={{ width: 56 }}>
						<div
							onClick={() => setActive('INFRASTRUCTURE')}
							style={{
								backgroundColor: 'white',
								borderRadius: '8px',
								padding: '0px 0px 0px 17px',
								height: '44px',
							}}
							className="flex items-center"
						>
							<Tooltip placement="right" title="Assets">
								<Icon
									component={WaveSvg}
									style={{ fontSize: '20px', cursor: 'pointer' }}
									rev={undefined}
								/>
							</Tooltip>
						</div>
						<div
							onClick={() => setActive('KPI')}
							style={{
								backgroundColor: 'white',
								borderRadius: '8px',
								padding: '0px 0px 0px 17px',
								height: '44px',
							}}
							className="flex items-center"
						>
							<Tooltip placement="right" title="KPIs">
								<Icon
									component={KpiSvg}
									style={{ fontSize: '20px', cursor: 'pointer' }}
									rev={undefined}
								/>
							</Tooltip>
						</div>
					</div>
				</Sider>
				<Sider
					style={{
						background: 'white',
						position: 'absolute',
						zIndex: 9,
						top: 75,
						left: 10,
						borderRadius: 5,
						height: context.showParamsCard && !context.isExpandedParamsCard
							? `calc(100% - 76px - ${context.compactCardHeight ?? 300}px)`
							: context.showParamsCard && context.isExpandedParamsCard
							? 'calc(100% - 76px - 50vh)'
							: 'calc(100% - 76px)',
						display: context.expandLeftMenu ? 'block' : 'none',
						overflow: 'auto',
						transition: 'height 0.3s ease-in-out',
					}}
					width={270}
				>
					<div>
						<Tab
							tabs={tabs}
							showContent
							orientation="horizontal"
							activeTab={tabActive || undefined}
							onTabChange={onChange}
						/>
						<div
							className="close"
							onClick={() =>
								setContext((prevContext) => ({ ...prevContext, expandLeftMenu: false }))
							}
						>
							<CloseOutlined rev={undefined} />
						</div>
					</div>
				</Sider>
			</Layout>

			<div className="absolute top-[90px] right-2 z-9 bg-white rounded-md p-1">
				<LayerList
					infraTypes={currentInfrastructureTypes}
					handleSelectedInfraTypes={(list) => {
						dispatch(setSelectedInfrastructureTypes(list));
					}}
				/>
			</div>

			<div style={{ position: 'absolute', left: 0, bottom: 10 }}>
				{pinnedKpis.length > 0 && (
					<Row
						gutter={[0, 100]}
						style={{ height: 100, columnGap: 10, marginLeft: 10, marginBottom: 15 }}
					>
						{pinnedKpis.map((card) => (
							<KpiCard
								key={card.id}
								kpi={card}
								onRefresh={handleGetKpis}
								onPinKpi={handlePinKpi}
							/>
						))}
					</Row>
				)}
			</div>

			{/* Digital Twins Side Panel */}
			{context.activatedDigitalTwinMode && (
				<SidePanel
					visible={true}
					onClose={handleCloseDigitalTwins}
					title="DIGITAL TWIN LENS"
					width={500}
					closable={true}
					maskClosable={false}
				>
					<DigitalTwinsContainer />
				</SidePanel>
			)}

			{/* Past Overflow Side Panel */}
			{context.activatedPastOverflowMode && (
				<SidePanel
					visible={true}
					onClose={handleClosePastOverflow}
					title={m.past_overflow_mode_title()}
					subtitle="MODE DESCRIPTION"
					width={420}
					closable={true}
					maskClosable={false}
					autoHeight
				>
					<PastOverflowSidePanel />
				</SidePanel>
			)}

			{/* MMS Side Panel */}
			{context.activatedMmsMode && (
				<SidePanel
					visible={true}
					onClose={handleCloseMms}
					title="Hydraulic Model Workbench"
					subtitle="Run various models and create their versions"
					width={500}
					closable={true}
					maskClosable={false}
				>
					<MmsSidePanel onClose={handleCloseMms} mmsMapData={mmsMapData} />
				</SidePanel>
			)}

			{context.activatedSensorMaintenanceMode && (
				<SensorMaintenanceLensSidebar onClose={handleCloseSensorMaintenance} />
			)}
		</>
	);
}

export default function Dashboard() {
	return (
		<DigitalTwinsContextProvider>
			<SensorMaintenanceLensProvider>
				<DashboardContent />
			</SensorMaintenanceLensProvider>
		</DigitalTwinsContextProvider>
	);
}
